import { createStore, createEffect, createEvent, sample } from "effector";
// ⚠️ Подстрой имя функции под свой API.
import { buildingsApi } from "../api/buildings";
import { BuildingInDB } from "../api/types";
import { $canDeleteComplex as $canDeleteBuilding } from "./auth";

export type BuildingCounters = {
  cold?: number;
  hot?: number;
  heat?: number;
  electricity?: number;
  all?: number;
};

export type Building = BuildingInDB & {
  counters?: BuildingCounters;
  incidents?: Record<string, number>;
};

function mapBuildingWithCounters(building: BuildingInDB): Building {
  const deviceCounts = building.device_counts || null;
  return {
    ...building,
    counters: {
      cold: deviceCounts?.hvs ?? 0,
      hot: deviceCounts?.gvs ?? 0,
      heat: deviceCounts?.heating ?? 0,
      electricity: deviceCounts?.electricity ?? 0,
      all: deviceCounts?.total ?? building.device_count ?? 0,
    },
  };
}

export const fetchBuildingsByComplexFx = createEffect(
  async (complexId: string | number) => {
    // ЗАМЕНИ: если у тебя другой метод/сигнатура
    const rawList = await buildingsApi.getBuildingsByComplexId(complexId);
    const list = rawList.map(mapBuildingWithCounters);
    return { complexId, list };
  }
);

export const createBuildingFx = createEffect(
  async ({
    complexId,
    name,
    address,
    latitude = null,
    longitude = null,
    manual_view_only = false,
    manual_submission_enabled = false,
    mobile_view_only = false,
    mobile_submission_enabled = false,
    manual_allow_account_registration = false,
    manual_allow_code_registration = false,
    manual_allow_meter_pair_registration = false,
    manual_submission_instructions = null,
  }: {
    complexId: string;
    name: string;
    address: string;
    latitude?: number | null;
    longitude?: number | null;
    manual_view_only?: boolean;
    manual_submission_enabled?: boolean;
    mobile_view_only?: boolean;
    mobile_submission_enabled?: boolean;
    manual_allow_account_registration?: boolean;
    manual_allow_code_registration?: boolean;
    manual_allow_meter_pair_registration?: boolean;
    manual_submission_instructions?: string | null;
  }) => {
    const created = await buildingsApi.createBuilding({
      name,
      address,
      latitude,
      longitude,
      residential_complex_id: complexId,
      manual_view_only,
      manual_submission_enabled,
      mobile_view_only,
      mobile_submission_enabled,
      manual_allow_account_registration,
      manual_allow_code_registration,
      manual_allow_meter_pair_registration,
      manual_submission_instructions,
    });
    return { complexId, building: mapBuildingWithCounters(created) };
  }
);
// 🔥 удаление
export const deleteBuildingFx = createEffect(async ({ id, force = false }: { id: string; force?: boolean }) => {
  await buildingsApi.deleteBuilding(id, force);
  return String(id);
});

// клик по удалению из любого места UI
export const deleteBuildingClicked = createEvent<{
  buildingId: string | number;
  complexId?: string | number;
  force?: boolean;
}>();

// пропускаем к эффекту только при наличии прав
sample({
  clock: deleteBuildingClicked,
  filter: $canDeleteBuilding,
  fn: ({ buildingId, force }) => ({ id: String(buildingId), force }),
  target: deleteBuildingFx,
});

// ids в процессе удаления (для кнопки-спиннера)
export const $deletingBuildingIds = createStore<Set<string>>(new Set())
  .on(deleteBuildingFx, (s, payload) => new Set(s).add(String(payload.id)))
  .on(deleteBuildingFx.finally, (s, { params }) => {
    const next = new Set(s);
    next.delete(String(params.id));
    return next;
  });
export const $buildingsByComplex = createStore<
  Record<string | number, Building[]>
>({})
  .on(fetchBuildingsByComplexFx.doneData, (state, { complexId, list }) => ({
    ...state,
    [complexId]: list,
  }))
  .on(createBuildingFx.doneData, (state, { complexId, building }) => {
    const key = String(complexId);
    const prev = state[key] || [];
    return { ...state, [key]: [building, ...prev] };
  })
  .on(deleteBuildingFx.doneData, (state, removedId) => {
    const next: Record<string, Building[]> = {};
    for (const key of Object.keys(state)) {
      next[key] = (state as any)[key].filter(
        (b: Building) => String(b.id) !== String(removedId)
      );
    }
    return next;
  });
